import React, { useState, useRef, useCallback } from "react";
import styled from "styled-components";
import Select from "react-select";
import Cell from "./cell";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimesCircle, faPlus, faCopy, faLayerGroup } from "@fortawesome/free-solid-svg-icons";
import Datetime from "react-datetime";
import "react-datetime/css/react-datetime.css";
import {
  FILTER_OPERATORS,
  getOperatorsForType,
  isFilterGroup,
  createFilterCondition,
  addConditionToGroup,
  operatorNeedsValue,
  isRangeOperator,
  getUrlWithFilters,
  migrateFromLegacyFormat,
  convertToLegacyFormat,
  createEmptyFilterGroup,
} from "./filterUtils";
import FilterGroupUI from "./FilterGroupUI";

const FilterInterface = ({
  columns,
  onFilterAdd,
  onRemoveFilter,
  filterList,
  dataRes,
  columnsMap,
  refRes,
  apiTarget,
  tableName,
  onFilterGroupChange,
  filterGroup: propFilterGroup,
  onApplyFilters,
  onAdvancedModeChange,
  isAdvancedMode: propIsAdvancedMode,
  filtersApplied,
}) => {
  const [isFilterMode, setIsFilterMode] = useState(false);
  const [filteredColumn, setFilteredColumn] = useState();
  const [condition, setCondition] = useState();
  const [cellValue, setCellValue] = useState(null);
  const [betweenStart, setBetweenStart] = useState(null);
  const [betweenEnd, setBetweenEnd] = useState(null);
  const editIndex = useRef(-1);
  const [showCopySuccess, setShowCopySuccess] = useState(false);
  const [useAdvancedMode, setUseAdvancedMode] = useState(propIsAdvancedMode || false);
  const [targetGroupForCondition, setTargetGroupForCondition] = useState(null); // Store which group to add condition to
  
  // Sync local advanced mode state with prop
  React.useEffect(() => {
    if (propIsAdvancedMode !== undefined && propIsAdvancedMode !== useAdvancedMode) {
      setUseAdvancedMode(propIsAdvancedMode);
    }
  }, [propIsAdvancedMode, useAdvancedMode]);
  
  // Convert filterList to filterGroup for advanced mode
  const getFilterGroup = useCallback(() => {
    if (filterList && filterList.length > 0) {
      return migrateFromLegacyFormat(filterList);
    }
    return createEmptyFilterGroup('AND');
  }, [filterList]);
  
  // Use prop filterGroup if provided, otherwise use internal state
  const [internalFilterGroup, setInternalFilterGroup] = useState(() => getFilterGroup());
  const filterGroup = propFilterGroup || internalFilterGroup;
  
  // Sync filterGroup when filterList changes (if not in advanced mode and no prop provided)
  React.useEffect(() => {
    if (!propFilterGroup && !useAdvancedMode && filterList) {
      setInternalFilterGroup(getFilterGroup());
    }
  }, [filterList, useAdvancedMode, getFilterGroup, propFilterGroup]);

  // Get operators based on column type
  const getConditions = (type, isReference = false) => {
    return getOperatorsForType(type, isReference);
  };

  // Copy link handler
  const handleCopyLink = useCallback(() => {
    const url = getUrlWithFilters(tableName, filterGroup);
    navigator.clipboard.writeText(url).then(() => {
      setShowCopySuccess(true);
      setTimeout(() => setShowCopySuccess(false), 2000);
    });
  }, [tableName, filterGroup]);

  // Render existing filters
  const _renderFilters = () => {
    if (filterList && filterList.length > 0) {
      return filterList.map((item, index) => {
        let displayValue;
        
        // Handle different value types
        if (Array.isArray(item.value)) {
          displayValue = item.value.map(e => e.title || e._id || e).join(", ");
          if (displayValue.length > 30) {
            displayValue = `${item.value.length} items selected`;
          }
        } else if (item.value && typeof item.value === 'object') {
          if (item.value.start !== undefined && item.value.end !== undefined) {
            // Between value
            displayValue = `${item.value.start} - ${item.value.end}`;
          } else {
            displayValue = item.value.title || item.value._id || JSON.stringify(item.value);
          }
        } else {
          displayValue = item.value?.toString() || '';
        }

        // For contains operator, strip the % wildcards for display
        if (item.condition?.value === "ILIKE" && typeof displayValue === 'string') {
          displayValue = displayValue.replace(/^%|%$/g, '');
        }

        // Get operator label
        const operatorLabel = item.condition?.label || 
          FILTER_OPERATORS[item.condition?.value]?.label || 
          item.condition?.value;

        // No value display for isEmpty/isNotEmpty/isNull/isNotNull
        const showValue = !['isEmpty', 'isNotEmpty', 'isNull', 'isNotNull'].includes(item.condition?.value);

        return (
          <FilterButton
            onClick={() => {
              const columnData = columnsMap.current[item.column];
              setFilteredColumn({ value: item.column, name: displayValue, type: columnData?.type });
              setCondition(item.condition);
              if (item.value && typeof item.value === 'object' && item.value.start !== undefined) {
                setBetweenStart(item.value.start);
                setBetweenEnd(item.value.end);
              } else {
                setCellValue(item.value);
              }
              setIsFilterMode(true);
              editIndex.current = index;
            }}
            key={`${item.column}${index}`}
          >
            <Row style={{ width: "100%", color: "#162d3d" }}>
              <FilterColumnName>{item.column}</FilterColumnName>
              <FilterOperator>{operatorLabel}</FilterOperator>
              {showValue && (
                <FilterValue>
                  {displayValue}
                </FilterValue>
              )}
              <RemoveButton
                onClick={(e) => {
                  onRemoveFilter?.(index);
                  e.stopPropagation();
                }}
              >
                <FontAwesomeIcon color={"#aa0b0b"} icon={faTimesCircle} />
              </RemoveButton>
            </Row>
          </FilterButton>
        );
      });
    }
  };

  const handleFilterGroupChange = useCallback((newGroup) => {
    // Always call parent's handler first if available (for complex groups or advanced mode)
    if (onFilterGroupChange) {
      onFilterGroupChange(newGroup);
    }
    
    if (propFilterGroup) {
      // If prop is provided, we're done (parent handles state)
      return;
    } else {
      // Otherwise update internal state
      setInternalFilterGroup(newGroup);
    }
    
    // Only convert to legacy format and update filterList if NOT in advanced mode
    // In advanced mode, we should use filterGroup directly
    if (!useAdvancedMode && !(newGroup?.operator === 'OR' || newGroup?.conditions?.some(isFilterGroup))) {
      // Convert to legacy format for backwards compatibility (simple AND groups only)
      const legacyFilters = convertToLegacyFormat(newGroup);
      
      if (Array.isArray(legacyFilters)) {
        // Update the parent component's filterList
        // Only update if the filters actually changed to avoid unnecessary re-renders
        const currentFiltersStr = JSON.stringify(filterList);
        const newFiltersStr = JSON.stringify(legacyFilters);
        
        if (onFilterAdd && currentFiltersStr !== newFiltersStr) {
          // Clear existing filters and add new ones
          // Use setTimeout to ensure all removals happen before additions
          setTimeout(() => {
            // Remove in reverse order to avoid index shifting issues
            for (let i = (filterList?.length || 0) - 1; i >= 0; i--) {
              onRemoveFilter?.(i);
            }
            // Add new filters after a brief delay to ensure removals are processed
            setTimeout(() => {
              legacyFilters.forEach((filter) => {
                onFilterAdd(filter, -1);
              });
            }, 10);
          }, 0);
        }
      }
    }
  }, [filterList, onFilterAdd, onRemoveFilter, onFilterGroupChange, propFilterGroup, useAdvancedMode]);

  const _renderQueryMode = () => {
    // Show advanced mode if filterGroup has OR or nested groups
    const hasComplexFilters = filterGroup?.operator === 'OR' || 
      (filterGroup?.conditions?.some && filterGroup.conditions.some(isFilterGroup));
    
    return (
      <div style={{ width: "100%" }}>
        {/* Toggle between simple and advanced mode */}
        <Row style={{ justifyContent: "space-between", padding: "8px 16px", borderBottom: "1px solid #dfe5eb" }}>
          <Text style={{ fontSize: "12px", color: "#7a92a5" }}>
            {useAdvancedMode || hasComplexFilters ? "Advanced Mode (OR groups enabled)" : "Simple Mode (AND only)"}
          </Text>
          <ToggleButton 
            onClick={() => {
              const newMode = !useAdvancedMode;
              setUseAdvancedMode(newMode);
              if (newMode && !propFilterGroup) {
                setInternalFilterGroup(getFilterGroup());
              }
              // Notify parent of advanced mode change
              if (onAdvancedModeChange) {
                onAdvancedModeChange(newMode);
              }
            }}
            active={useAdvancedMode || hasComplexFilters}
          >
            <FontAwesomeIcon icon={faLayerGroup} style={{ marginRight: "4px" }} />
            {useAdvancedMode || hasComplexFilters ? "Advanced" : "Simple"}
          </ToggleButton>
        </Row>

        {useAdvancedMode || hasComplexFilters ? (
          // Advanced mode with FilterGroupUI
          <div style={{ padding: "12px" }}>
            {filterGroup && filterGroup.conditions && filterGroup.conditions.length > 0 ? (
              <FilterGroupUI
                filterGroup={filterGroup}
                onFilterGroupChange={handleFilterGroupChange}
                columnsMap={columnsMap}
                onAddCondition={(parentGroup) => {
                  setTargetGroupForCondition(parentGroup);
                  setIsFilterMode(true);
                  editIndex.current = -1;
                }}
              />
            ) : (
              <Text style={{ padding: "15px 24px", textAlign: "center" }}>
                No filters applied
              </Text>
            )}
            <Row style={{ justifyContent: "space-between", padding: "8px", marginTop: "12px" }}>
              <ActionButton onClick={() => {
                setTargetGroupForCondition(filterGroup); // Add to root group
                setIsFilterMode(true);
              }}>
                <FontAwesomeIcon icon={faPlus} style={{ marginRight: "8px" }} />
                Add Condition
              </ActionButton>
              {filterGroup && filterGroup.conditions && filterGroup.conditions.length > 0 && (
                <CopyLinkButton onClick={handleCopyLink}>
                  <FontAwesomeIcon icon={faCopy} style={{ marginRight: "8px" }} />
                  {showCopySuccess ? "Copied!" : "Copy Link"}
                </CopyLinkButton>
              )}
            </Row>
            {/* Apply Filters button - only show when filters exist and haven't been applied */}
            {filterGroup && filterGroup.conditions && filterGroup.conditions.length > 0 && (
              <ApplyFiltersRow>
                <ApplyFiltersButton 
                  onClick={() => {
                    if (onApplyFilters) {
                      onApplyFilters();
                    }
                  }}
                  disabled={filtersApplied}
                >
                  {filtersApplied ? "Filters Applied" : "Apply Filters"}
                </ApplyFiltersButton>
                {!filtersApplied && (
                  <PendingIndicator>
                    Filters pending - click "Apply Filters" to search
                  </PendingIndicator>
                )}
              </ApplyFiltersRow>
            )}
          </div>
        ) : (
          // Simple mode (existing flat list)
          <>
            {(!filterList || filterList.length === 0) && (
              <Filters>
                <Text style={{ justifyContent: "center", padding: "15px 24px" }}>
                  {"No filters applied"}
                </Text>
              </Filters>
            )}
            <FilterQuery>
              {_renderFilters()}
              <Row style={{ justifyContent: "space-between", padding: "0 8px" }}>
                <ActionButton onClick={() => setIsFilterMode(true)}>
                  <FontAwesomeIcon icon={faPlus} style={{ marginRight: "8px" }} />
                  {"Add Filter"}
                </ActionButton>
                {filterList && filterList.length > 0 && (
                  <CopyLinkButton onClick={handleCopyLink}>
                    <FontAwesomeIcon icon={faCopy} style={{ marginRight: "8px" }} />
                    {showCopySuccess ? "Copied!" : "Copy Link"}
                  </CopyLinkButton>
                )}
              </Row>
            </FilterQuery>
          </>
        )}
      </div>
    );
  };

  const goBack = () => {
    setIsFilterMode(false);
    setFilteredColumn(null);
    setCondition(null);
    setCellValue(null);
    setBetweenStart(null);
    setBetweenEnd(null);
    setTargetGroupForCondition(null);
  };

  const handleAddFilter = () => {
    const columnData = columnsMap.current[filteredColumn?.value];
    const operatorValue = condition?.value || condition;
    
    let finalValue;
    if (isRangeOperator(operatorValue)) {
      finalValue = { start: betweenStart, end: betweenEnd };
    } else if (operatorValue === "ILIKE" || operatorValue === "contains") {
      finalValue = cellValue;
    } else if (!operatorNeedsValue(operatorValue)) {
      finalValue = null;
    } else if (columnData?.type === "Boolean" && cellValue === null) {
      finalValue = false;
    } else {
      finalValue = cellValue || "";
    }

    // If in advanced mode, add to filterGroup
    if (useAdvancedMode || filterGroup?.operator === 'OR' || filterGroup?.conditions?.some(isFilterGroup)) {
      const newCondition = createFilterCondition(
        filteredColumn.value,
        operatorValue,
        finalValue
      );
      
      // Add to target group if specified, otherwise add to root group
      const groupToUpdate = targetGroupForCondition || filterGroup || createEmptyFilterGroup('AND');
      const updatedTargetGroup = addConditionToGroup(groupToUpdate, newCondition);
      
      // If we added to a nested group, we need to update the root group with the modified nested group
      if (targetGroupForCondition && targetGroupForCondition !== filterGroup) {
        // Find the nested group in the root group and replace it
        const updateNestedGroupInRoot = (root, target, updated) => {
          if (root === target) {
            return updated;
          }
          if (root.conditions) {
            return {
              ...root,
              conditions: root.conditions.map(item => 
                isFilterGroup(item) ? updateNestedGroupInRoot(item, target, updated) : item
              )
            };
          }
          return root;
        };
        const updatedRootGroup = updateNestedGroupInRoot(filterGroup, targetGroupForCondition, updatedTargetGroup);
        handleFilterGroupChange(updatedRootGroup);
      } else {
        // Added to root group
        handleFilterGroupChange(updatedTargetGroup);
      }
    } else {
      // Simple mode - use legacy format
      let filterCondition;
      if (condition && typeof condition === 'object' && condition.value) {
        filterCondition = condition;
        if (operatorValue === "contains") {
          finalValue = `%${cellValue}%`;
          filterCondition = { value: "ILIKE", label: "Contains" };
        }
      } else {
        const operatorMap = {
          'equals': { value: '=', label: 'Equal' },
          'notEquals': { value: '!=', label: 'Not Equal' },
          'contains': { value: 'ILIKE', label: 'Contains' },
          'notContains': { value: 'notILIKE', label: 'Does Not Contain' },
          'in': { value: 'IN', label: 'In List' },
          'greaterThan': { value: '>', label: 'Greater Than' },
          'lessThan': { value: '<', label: 'Less Than' },
          'greaterThanOrEqual': { value: '>=', label: 'Greater Than or Equal' },
          'lessThanOrEqual': { value: '<=', label: 'Less Than or Equal' },
          'between': { value: 'BETWEEN', label: 'Between' },
          'isEmpty': { value: 'isEmpty', label: 'Is Empty' },
          'isNotEmpty': { value: 'isNotEmpty', label: 'Is Not Empty' },
          'isNull': { value: 'isNull', label: 'Is Null' },
          'isNotNull': { value: 'isNotNull', label: 'Is Not Null' },
        };
        filterCondition = operatorMap[operatorValue] || condition;
        
        if (operatorValue === "contains") {
          finalValue = `%${cellValue}%`;
        }
      }

      onFilterAdd &&
        onFilterAdd(
          {
            column: filteredColumn.value,
            value: finalValue,
            condition: filterCondition,
          },
          editIndex.current
        );
    }
    
    editIndex.current = -1;
    goBack();
  };

  const _renderValueInput = () => {
    const columnData = columnsMap.current[filteredColumn?.value];
    const operatorValue = condition?.value || condition;
    const type = columnData?.reference ? "reference" : columnData?.type;

    // No value input needed for these operators
    if (!operatorNeedsValue(operatorValue)) {
      return (
        <Text style={{ padding: "15px 20px", color: "#7a92a5" }}>
          No value needed for this operator
        </Text>
      );
    }

    // Between operator - show two inputs
    if (isRangeOperator(operatorValue)) {
      return (
        <div style={{ padding: "0 20px" }}>
          <Text style={{ padding: "15px 0" }}>{"Enter Range"}</Text>
          <BetweenContainer>
            {(type === "Date" || type === "timestamp") ? (
              <>
                <DateInputWrapper>
                  <Datetime
                    inputProps={{ placeholder: "Start date" }}
                    closeOnSelect
                    value={betweenStart ? new Date(betweenStart) : null}
                    onChange={(val) => setBetweenStart(val ? new Date(val).toISOString() : null)}
                  />
                </DateInputWrapper>
                <span style={{ margin: "0 10px" }}>to</span>
                <DateInputWrapper>
                  <Datetime
                    inputProps={{ placeholder: "End date" }}
                    closeOnSelect
                    value={betweenEnd ? new Date(betweenEnd) : null}
                    onChange={(val) => setBetweenEnd(val ? new Date(val).toISOString() : null)}
                  />
                </DateInputWrapper>
              </>
            ) : (
              <>
                <NumberInput
                  type="number"
                  placeholder="Start"
                  value={betweenStart || ''}
                  onChange={(e) => setBetweenStart(e.target.value ? Number(e.target.value) : null)}
                />
                <span style={{ margin: "0 10px" }}>to</span>
                <NumberInput
                  type="number"
                  placeholder="End"
                  value={betweenEnd || ''}
                  onChange={(e) => setBetweenEnd(e.target.value ? Number(e.target.value) : null)}
                />
              </>
            )}
          </BetweenContainer>
        </div>
      );
    }

    // Date input
    if (type === "Date" || type === "timestamp") {
      return (
        <div style={{ padding: "0 20px" }}>
          <Text style={{ padding: "15px 0" }}>{"Select Date"}</Text>
          <DateInputWrapper>
            <Datetime
              inputProps={{ placeholder: "Select date..." }}
              closeOnSelect
              value={cellValue ? new Date(cellValue) : null}
              onChange={(val) => setCellValue(val ? new Date(val).toISOString() : null)}
            />
          </DateInputWrapper>
        </div>
      );
    }

    // Number input
    if (type === "Number" || type === "real") {
      return (
        <div style={{ padding: "0 20px" }}>
          <Text style={{ padding: "15px 0" }}>{"Enter Number"}</Text>
          <NumberInput
            type="number"
            placeholder="Enter a number..."
            value={cellValue || ''}
            onChange={(e) => setCellValue(e.target.value ? Number(e.target.value) : null)}
          />
        </div>
      );
    }

    // Default: use Cell component (handles reference, boolean, text, etc.)
    return (
      <div style={{ padding: "0 20px" }}>
        <Text style={{ padding: "15px 0" }}>{"Enter a Value"}</Text>
        <Cell
          isFilter
          width={"295px"}
          refRes={refRes}
          refColumn={columnData?.reference}
          refItems={
            columnData?.reference
              ? dataRes
                  .map((d) => d[filteredColumn?.value])
                  .filter((d) => d)
              : null
          }
          onCellChange={(value) => {
            setCellValue(value);
          }}
          style={{
            width: "300px",
            border: "1px solid #b7b7b7",
            borderRadius: "4px",
          }}
          cell={filteredColumn?.name}
          type={type}
          apiTarget={apiTarget}
        />
      </div>
    );
  };

  const _renderFilterMode = () => {
    const columnData = columnsMap.current[filteredColumn?.value];
    const columnType = columnData?.reference ? "reference" : columnData?.type;

    return (
      <div style={{ width: "100%" }}>
        <BackToAllField
          onClick={() => {
            editIndex.current = -1;
            goBack();
          }}
        >
          {"← All fields"}
        </BackToAllField>
        
        {/* Column Selection */}
        <div style={{ padding: "0 20px" }}>
          <Text style={{ padding: "15px 0" }}>{"Select Field"}</Text>
          <Select
            className="basic-single"
            classNamePrefix="select"
            placeholder="Choose a column..."
            value={filteredColumn ? { label: filteredColumn.value, value: filteredColumn.value } : null}
            styles={{
              control: (provided) => ({
                ...provided,
                width: 300,
              }),
            }}
            onChange={(event) => {
              setFilteredColumn(event);
              setCondition(null); // Reset condition when column changes
              setCellValue(null);
            }}
            isClearable={false}
            isSearchable
            options={
              columns?.map((item) => ({
                value: item.name,
                label: item.name,
                type: item.type,
              })) || []
            }
          />
        </div>
        
        {/* Operator Selection */}
        {filteredColumn && (
          <div style={{ padding: "0 20px" }}>
            <Text style={{ padding: "15px 0" }}>{"Choose Operator"}</Text>
            <Select
              value={condition ? { 
                label: condition.label || FILTER_OPERATORS[condition.value]?.label || condition,
                value: condition.value || condition 
              } : null}
              className="basic-single"
              classNamePrefix="select"
              placeholder="Select operator..."
              styles={{
                control: (provided) => ({
                  ...provided,
                  width: 300,
                }),
              }}
              onChange={(event) => {
                setCondition(event);
                // Reset values when operator changes
                setCellValue(null);
                setBetweenStart(null);
                setBetweenEnd(null);
              }}
              isClearable={false}
              isSearchable
              options={
                getConditions(columnType, !!columnData?.reference)?.map((item) => ({
                  value: item.value,
                  label: item.label,
                })) || []
              }
            />
          </div>
        )}
        
        {/* Value Input */}
        {condition && _renderValueInput()}
        
        {/* Action Buttons */}
        <Row style={{ justifyContent: "flex-end", padding: "0 15px" }}>
          <CancelButton onClick={goBack}>
            {"Cancel"}
          </CancelButton>
          <AddFilterButton
            onClick={handleAddFilter}
            disabled={!filteredColumn || !condition}
          >
            {editIndex.current > -1 ? "Update Filter" : "Add Filter"}
          </AddFilterButton>
        </Row>
      </div>
    );
  };

  return (
    <Wrapper isFilterMode={isFilterMode}>
      {isFilterMode ? _renderFilterMode() : _renderQueryMode()}
    </Wrapper>
  );
};

export default FilterInterface;

// Styled Components
const Wrapper = styled.div`
  display: flex;
  position: relative;
  flex-direction: column;
  z-index: 9999;
  align-items: center;
  width: 360px;
  border-radius: 8px;
  box-shadow: 0 5px 12px 0 rgba(22, 45, 60, 0.3);
  margin: 10px;
  max-height: 598px;
  background-color: white;
`;

const Filters = styled.div`
  position: relative;
  overflow: auto;
  flex-grow: 1;
  max-height: 330px;
`;

const FilterButton = styled.button`
  display: flex;
  width: 100%;
  min-height: 48px;
  padding: 8px 18px 8px 18px;
  border: 0;
  background-color: #fff;
  color: #3899ec;
  cursor: pointer;
  align-items: center;
  border-bottom: 1px solid #eef3f7;

  &:hover {
    background-color: #eaf7ff;
    border-color: transparent;
  }
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 18px;
  border: 0;
  background-color: #fff;
  color: #3899ec;
  cursor: pointer;
  font-weight: 500;

  &:hover {
    background-color: #eaf7ff;
  }
`;

const CopyLinkButton = styled(ActionButton)`
  color: #7a92a5;
  font-size: 12px;
  
  &:hover {
    color: #3899ec;
  }
`;

const FilterQuery = styled.div`
  flex-grow: 0;
  border-top: 1px solid #dfe5eb;
  width: 100%;
`;

const Text = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  font-stretch: normal;
  letter-spacing: normal;
  color: #7a92a5;
  justify-content: flex-start;
`;

const BackToAllField = styled.button`
  margin: 18px 24px;
  height: 30px;
  padding: 0 18px;
  background-color: #fff;
  color: #3899ec;
  cursor: pointer;
  border-radius: 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  outline: 0;
  border: 1px solid #3899ec;

  &:hover {
    background-color: #eaf7ff;
  }
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  z-index: 99999;
  align-items: center;
`;

const FilterColumnName = styled.div`
  font-weight: bold;
  margin-right: 5px;
  white-space: nowrap;
`;

const FilterOperator = styled.div`
  margin-right: 5px;
  color: #7a92a5;
  white-space: nowrap;
`;

const FilterValue = styled.div`
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: start;
`;

const RemoveButton = styled.div`
  margin-left: 8px;
  cursor: pointer;
  opacity: 0.7;
  
  &:hover {
    opacity: 1;
  }
`;

const CancelButton = styled.button`
  margin: 18px 12px 18px 24px;
  height: 36px;
  padding: 0 18px;
  background-color: #fff;
  color: #3899ec;
  cursor: pointer;
  border-radius: 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  outline: 0;
  border: 1px solid #3899ec;

  &:hover {
    background-color: #eaf7ff;
  }
`;

const AddFilterButton = styled.button`
  margin: 18px 24px 18px 0;
  height: 36px;
  padding: 0 24px;
  background-color: #3899ec;
  color: white;
  cursor: pointer;
  border-radius: 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  outline: 0;
  border: none;
  font-weight: 500;

  &:hover {
    background-color: #4eb7f5;
  }

  &:disabled {
    background-color: #b0b0b0;
    cursor: not-allowed;
  }
`;

const BetweenContainer = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
`;

const DateInputWrapper = styled.div`
  flex: 1;
  min-width: 120px;
  
  .rdt {
    width: 100%;
  }
  
  .rdtPicker {
  width: 100%;
  min-width: 400px;
  }
  
  input {
    width: 100%;
    padding: 8px 12px;
    border: 1px solid #b7b7b7;
    border-radius: 4px;
    font-size: 14px;
    
    &:focus {
      border-color: #3899ec;
      outline: none;
    }
  }
`;

const NumberInput = styled.input`
  flex: 1;
  min-width: 80px;
  padding: 8px 12px;
  border: 1px solid #b7b7b7;
  border-radius: 4px;
  font-size: 14px;
  
  &:focus {
    border-color: #3899ec;
    outline: none;
  }
  
  &::placeholder {
    color: #b0b0b0;
  }
`;

const ToggleButton = styled.button`
  display: flex;
  align-items: center;
  padding: 6px 12px;
  border: 1px solid ${({ active }) => active ? '#3899ec' : '#dfe5eb'};
  background-color: ${({ active }) => active ? '#eaf7ff' : 'white'};
  color: ${({ active }) => active ? '#3899ec' : '#7a92a5'};
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: ${({ active }) => active ? '#d4edfc' : '#f5f5f5'};
  }
`;

const ApplyFiltersRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: 12px 8px;
  border-top: 1px solid #dfe5eb;
  margin-top: 8px;
`;

const ApplyFiltersButton = styled.button`
  padding: 12px 24px;
  background-color: ${({ disabled }) => disabled ? '#b0d4f1' : '#3899ec'};
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: ${({ disabled }) => disabled ? 'default' : 'pointer'};
  transition: all 0.2s;
  
  &:hover {
    background-color: ${({ disabled }) => disabled ? '#b0d4f1' : '#2a7ac4'};
  }
`;

const PendingIndicator = styled.div`
  margin-top: 8px;
  padding: 8px 12px;
  background-color: #fff8e6;
  border: 1px solid #ffc107;
  border-radius: 4px;
  color: #856404;
  font-size: 12px;
  text-align: center;
`;
